<template>
    <Teleport to="body">
        <Transition name="bond-v2-dialog-fade">
            <div v-if="modelValue" class="bond-v2-cashflow-dialog-overlay" @click="closeDialog">
                <div
                    ref="dialogRef"
                    class="bond-v2-cashflow-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="bond-v2-cashflow-dialog-title"
                    @click.stop
                >
                    <button
                        ref="closeButtonRef"
                        class="bond-v2-cashflow-dialog__close"
                        type="button"
                        aria-label="Close cashflow dialog"
                        @click="closeDialog"
                    >
                        <VIcon size="22" aria-hidden="true">mdi-close</VIcon>
                    </button>

                    <h2 id="bond-v2-cashflow-dialog-title" class="bond-v2-cashflow-dialog__title">
                        Cashflow Timeline
                    </h2>

                    <BondV2Cashflow
                        title-id="bond-v2-cashflow-dialog-section-title"
                        dialog-mode
                        :units="units"
                        :min-units="minUnits"
                        :max-units="maxUnits"
                        :lot-size="lotSize"
                        @units-changed="handleUnitsChanged"
                    />
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import BondV2Cashflow from './BondV2Cashflow.vue'

defineOptions({ name: 'BondV2CashflowDialog' })

interface Props {
    modelValue: boolean
    units: number
    minUnits: number
    maxUnits: number
    lotSize: number
}

const props = defineProps<Props>()
const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    'units-changed': [units: number]
}>()

let lockedScrollY = 0
const dialogRef = ref<HTMLElement | null>(null)
const closeButtonRef = ref<HTMLButtonElement | null>(null)
let previouslyFocusedElement: HTMLElement | null = null

const closeDialog = () => {
    emit('update:modelValue', false)
}

const handleUnitsChanged = (units: number) => {
    emit('units-changed', units)
}

const lockBodyScroll = () => {
    lockedScrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${lockedScrollY}px`
    document.body.style.width = '100%'
    document.body.style.overflowY = 'scroll'
}

const unlockBodyScroll = () => {
    const scrollY = document.body.style.top
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflowY = ''
    window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
}

const handleKeydown = (event: KeyboardEvent) => {
    if (!props.modelValue) return

    if (event.key === 'Escape') {
        closeDialog()
        return
    }

    if (event.key !== 'Tab') return

    const focusableElements = dialogRef.value?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    const focusable = Array.from(focusableElements ?? []).filter((element) => !element.hasAttribute('disabled'))

    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
    }
}

watch(() => props.modelValue, async (isOpen) => {
    if (isOpen) {
        previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null
        lockBodyScroll()
        await nextTick()
        closeButtonRef.value?.focus()
    } else {
        unlockBodyScroll()
        previouslyFocusedElement?.focus()
        previouslyFocusedElement = null
    }
})

onMounted(() => {
    window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown)
    if (props.modelValue) {
        unlockBodyScroll()
    }
})
</script>

<style scoped>
.bond-v2-cashflow-dialog-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(27, 37, 89, 0.5);
}

.bond-v2-cashflow-dialog {
    position: relative;
    width: min(960px, 100%);
    max-height: 92vh;
    background: #fff;
    border-radius: 12px;
    padding: 28px 32px 32px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.24);
    box-sizing: border-box;
}

.bond-v2-cashflow-dialog__close {
    position: absolute;
    top: 18px;
    right: 18px;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #1b2559;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.bond-v2-cashflow-dialog__close:hover {
    background: #f5f8ff;
}

.bond-v2-cashflow-dialog :focus-visible {
    outline: 3px solid #0027af;
    outline-offset: 3px;
}

.bond-v2-cashflow-dialog__title {
    margin: 0 44px 18px;
    color: #1b2559;
    text-align: center;
    font-family: Inter, var(--font-family-primary);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
}

.bond-v2-dialog-fade-enter-active,
.bond-v2-dialog-fade-leave-active {
    transition: opacity 0.2s ease;
}

.bond-v2-dialog-fade-enter-from,
.bond-v2-dialog-fade-leave-to {
    opacity: 0;
}

.bond-v2-dialog-fade-enter-active .bond-v2-cashflow-dialog,
.bond-v2-dialog-fade-leave-active .bond-v2-cashflow-dialog {
    transition: transform 0.2s ease, opacity 0.2s ease;
}

.bond-v2-dialog-fade-enter-from .bond-v2-cashflow-dialog,
.bond-v2-dialog-fade-leave-to .bond-v2-cashflow-dialog {
    opacity: 0;
    transform: scale(0.96);
}

@media (prefers-reduced-motion: reduce) {
    .bond-v2-dialog-fade-enter-active,
    .bond-v2-dialog-fade-leave-active,
    .bond-v2-dialog-fade-enter-active .bond-v2-cashflow-dialog,
    .bond-v2-dialog-fade-leave-active .bond-v2-cashflow-dialog {
        transition-duration: 0.01ms !important;
    }
}

@media (max-width: 768px) {
    .bond-v2-cashflow-dialog-overlay {
        align-items: center;
        padding: 16px;
    }

    .bond-v2-cashflow-dialog {
        width: min(100%, 520px);
        max-height: 88vh;
        border-radius: 12px;
        padding: 20px 0 16px;
    }

    .bond-v2-cashflow-dialog__close {
        top: 12px;
        right: 12px;
    }

    .bond-v2-cashflow-dialog__title {
        margin: 0 52px 14px;
        font-size: 14px;
        line-height: 22px;
    }
}
</style>
